"use client"

/**
 * AuthProvider — bridges the imperative `authStorage` module to React state.
 *
 * Responsibilities:
 *  1. Bootstrap the in-memory token cache from persistent storage at app
 *     start, so the first synchronous `getToken()` call has a value.
 *  2. Subscribe to token changes and surface `hasToken` reactively, so
 *     AuthGuard re-evaluates and React Query re-fetches after login/logout
 *     no matter where they originate (e.g. from `apiClient.auth.login`).
 *  3. Invalidate stale profile caches on token transitions.
 *
 * Profile fetching itself stays in `useAuth` / `useFanAuth` (React Query).
 */
import { createContext, useContext, useEffect, useState, ReactNode } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { authStorage } from "./auth-storage"

interface AuthContextValue {
  /** True once the persisted token has been loaded into the in-memory cache. */
  isReady: boolean
  /**
   * Reactive token presence. Stays in sync with `authStorage` via the
   * subscribe API, so login/logout from anywhere in the app (including
   * inside `apiClient`) triggers a re-render here.
   */
  hasToken: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [isReady, setIsReady] = useState(false)
  const [hasToken, setHasToken] = useState(false)

  useEffect(() => {
    let cancelled = false
    authStorage.bootstrap().then((token) => {
      if (cancelled) return
      setHasToken(!!token)
      setIsReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    return authStorage.subscribe((token) => {
      const next = !!token
      setHasToken((prev) => {
        if (prev === next) return prev
        // Token transitions: drop cached profile data so dependent queries
        // refetch under the new identity (or clear after logout).
        if (next) {
          queryClient.invalidateQueries({ queryKey: ["auth"] })
          queryClient.invalidateQueries({ queryKey: ["fan"] })
        } else {
          queryClient.clear()
        }
        return next
      })
    })
  }, [queryClient])

  return (
    <AuthContext.Provider value={{ isReady, hasToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthSession(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuthSession must be used within an AuthProvider")
  }
  return ctx
}
